package com.shadowings.gero.presentation.viewmodel

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.shadowings.gero.domain.model.GameConstants
import com.shadowings.gero.domain.model.ThemeMode
import com.shadowings.gero.domain.model.UserData
import com.shadowings.gero.domain.repository.AuthRepository
import com.shadowings.gero.domain.repository.CloudSyncRepository
import com.shadowings.gero.domain.repository.UserDataRepository
import com.shadowings.gero.presentation.model.ProfileUiState
import com.shadowings.gero.presentation.widget.notifyStrikeWidgetDataChanged
import com.shadowings.gero.util.setAppLanguage
import com.shadowings.gero.domain.usecase.ScheduleQuizReminderUseCase
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.flow.SharingStarted
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.combine
import kotlinx.coroutines.flow.first
import kotlinx.coroutines.flow.stateIn
import kotlinx.coroutines.launch

import com.shadowings.gero.domain.repository.AnalyticsRepository

class ProfileViewModel(
    private val userDataRepository: UserDataRepository,
    private val authRepository: AuthRepository,
    private val cloudSyncRepository: CloudSyncRepository,
    private val analyticsRepository: AnalyticsRepository,
    private val scheduleQuizReminderUseCase: ScheduleQuizReminderUseCase,
) : ViewModel() {

    private val initialUserData =
        userDataRepository.currentUserDataOrNull ?: UserData()

    private val initialUiState = ProfileUiState(
        userData = initialUserData,
        testMode = (userDataRepository.testMode as? StateFlow<Boolean>)?.value ?: false,
        showCheats = (userDataRepository.showCheats as? StateFlow<Boolean>)?.value ?: false,
        themeMode = (userDataRepository.themeMode as? StateFlow<ThemeMode>)?.value ?: ThemeMode.System,
        soundEnabled = (userDataRepository.soundEnabled as? StateFlow<Boolean>)?.value ?: true,
        hapticEnabled = (userDataRepository.hapticEnabled as? StateFlow<Boolean>)?.value ?: true,
        hideOptions = (userDataRepository.hideOptions as? StateFlow<Boolean>)?.value ?: false,
        reminderHour = (userDataRepository.reminderHour as? StateFlow<Int>)?.value ?: 21,
        reminderMinute = (userDataRepository.reminderMinute as? StateFlow<Int>)?.value ?: 0,
        reminderEnabled = (userDataRepository.reminderEnabled as? StateFlow<Boolean>)?.value ?: true,
        isSyncing = (cloudSyncRepository.isSyncing as? StateFlow<Boolean>)?.value ?: false
    )

    private data class ReminderState(
        val enabled: Boolean,
        val hour: Int,
        val minute: Int
    )

    private val reminderState = combine(
        userDataRepository.reminderEnabled,
        userDataRepository.reminderHour,
        userDataRepository.reminderMinute
    ) { enabled, hour, minute ->
        ReminderState(enabled, hour, minute)
    }

    private data class FeedbackState(
        val soundEnabled: Boolean,
        val hapticEnabled: Boolean
    )

    private val feedbackState = combine(
        userDataRepository.soundEnabled,
        userDataRepository.hapticEnabled
    ) { soundEnabled, hapticEnabled ->
        FeedbackState(soundEnabled, hapticEnabled)
    }

    private data class ProfileSettingsState(
        val testMode: Boolean,
        val showCheats: Boolean,
        val themeMode: ThemeMode,
        val feedback: FeedbackState,
        val reminder: ReminderState
    )

    private val settingsState = combine(
        userDataRepository.testMode,
        userDataRepository.showCheats,
        userDataRepository.themeMode,
        feedbackState,
        reminderState
    ) { testMode, showCheats, themeMode, feedback, reminder ->
        ProfileSettingsState(testMode, showCheats, themeMode, feedback, reminder)
    }

    private data class LocalProfileState(
        val userData: UserData,
        val settings: ProfileSettingsState,
    )

    private val localProfileState = combine(
        userDataRepository.userData,
        settingsState
    ) { userData, settings ->
        LocalProfileState(userData, settings)
    }

    val uiState: StateFlow<ProfileUiState> = combine(
        localProfileState,
        authRepository.currentUser,
        cloudSyncRepository.isSyncing,
        userDataRepository.hideOptions
    ) { localState, currentUser, isSyncing, hideOptions ->
        ProfileUiState(
            userData = localState.userData,
            testMode = localState.settings.testMode,
            showCheats = localState.settings.showCheats,
            themeMode = localState.settings.themeMode,
            soundEnabled = localState.settings.feedback.soundEnabled,
            hapticEnabled = localState.settings.feedback.hapticEnabled,
            hideOptions = hideOptions,
            reminderEnabled = localState.settings.reminder.enabled,
            reminderHour = localState.settings.reminder.hour,
            reminderMinute = localState.settings.reminder.minute,
            userEmail = currentUser?.email,
            isLoggedIn = currentUser != null,
            isSyncing = isSyncing
        )
    }.stateIn(viewModelScope, SharingStarted.WhileSubscribed(5000), initialUiState)

    fun signOut() {
        viewModelScope.launch {
            authRepository.signOut()
            analyticsRepository.logSignOut()
        }
    }

    fun onCharacterTabClick(index: Int) {
        when (index) {
            0 -> analyticsRepository.logTabAlyn()
            1 -> analyticsRepository.logTabNilo()
            2 -> analyticsRepository.logTabZeta()
            3 -> analyticsRepository.logTabMasko()
        }
    }

    fun setTestMode(enabled: Boolean) =
        viewModelScope.launch(Dispatchers.Default) {
            userDataRepository.setTestMode(enabled)
            analyticsRepository.logTestModeToggled(enabled)
        }

    fun unlockCheats() =
        viewModelScope.launch(Dispatchers.Default) {
            userDataRepository.setShowCheats(true)
        }

    fun setLanguage(languageCode: String) =
        viewModelScope.launch(Dispatchers.Default) {
            // Apply the language to the system (best-effort – may be a no-op in test environments)
            try { setAppLanguage(languageCode) } catch (_: Throwable) { }
            // Then save it to DataStore
            userDataRepository.setLanguage(languageCode)
        }

    fun setThemeMode(themeMode: ThemeMode) =
        viewModelScope.launch(Dispatchers.Default) {
            userDataRepository.setThemeMode(themeMode)
        }

    fun setSoundEnabled(enabled: Boolean) =
        viewModelScope.launch(Dispatchers.Default) {
            userDataRepository.setSoundEnabled(enabled)
        }

    fun setHapticEnabled(enabled: Boolean) =
        viewModelScope.launch(Dispatchers.Default) {
            userDataRepository.setHapticEnabled(enabled)
        }

    fun setHideOptions(enabled: Boolean) =
        viewModelScope.launch(Dispatchers.Default) {
            userDataRepository.setHideOptions(enabled)
        }

    fun setReminderTime(hour: Int, minute: Int) =
        viewModelScope.launch(Dispatchers.Default) {
            userDataRepository.setReminderTime(hour, minute)
            scheduleQuizReminderUseCase()
        }

    fun setReminderEnabled(enabled: Boolean) =
        viewModelScope.launch(Dispatchers.Default) {
            userDataRepository.setReminderEnabled(enabled)
            scheduleQuizReminderUseCase()
        }

    fun addStars() =
        viewModelScope.launch(Dispatchers.Default) { userDataRepository.addStars() }

    fun unlockAllLessons() =
        viewModelScope.launch(Dispatchers.Default) { userDataRepository.unlockAllLessons() }

    fun unlockLessonsUpToChapter9() =
        viewModelScope.launch(Dispatchers.Default) { userDataRepository.unlockLessonsUpToChapter9() }

    fun clearWrongAnswers() =
        viewModelScope.launch(Dispatchers.Default) { userDataRepository.clearWrongAnswers() }

    fun addHearts(amount: Int) =
        viewModelScope.launch(Dispatchers.Default) {
            val current = userDataRepository.userData.first().hearts
            userDataRepository.setHearts(current + amount)
        }

    fun addExperience(amount: Int) =
        viewModelScope.launch(Dispatchers.Default) {
            userDataRepository.addExperience(amount)
        }

    fun removeHearts(amount: Int = GameConstants.DEFAULT_HEARTS) =
        viewModelScope.launch(Dispatchers.Default) {
            val current = userDataRepository.userData.first().hearts
            userDataRepository.setHearts(maxOf(0, current - amount))
        }

    fun resetData() =
        viewModelScope.launch(Dispatchers.Default) {
            val currentUser = authRepository.currentUser.first()
            cloudSyncRepository.resetAllData(currentUser?.uid)
            notifyStrikeWidgetDataChanged()
            analyticsRepository.logDataReset()
        }

    fun setAlynColor(categoryKey: String, index: Int) =
        viewModelScope.launch(Dispatchers.Default) {
            userDataRepository.setAlynColor(categoryKey, index)
            analyticsRepository.logCharacterCustomized("alyn", categoryKey, index)
        }

    fun setNiloColor(categoryKey: String, index: Int) =
        viewModelScope.launch(Dispatchers.Default) {
            userDataRepository.setNiloColor(categoryKey, index)
            analyticsRepository.logCharacterCustomized("nilo", categoryKey, index)
        }

    fun resetAlynColors() =
        viewModelScope.launch(Dispatchers.Default) {
            userDataRepository.resetAlynColors()
            analyticsRepository.logCharacterReset("alyn")
        }

    fun resetNiloColors() =
        viewModelScope.launch(Dispatchers.Default) {
            userDataRepository.resetNiloColors()
            analyticsRepository.logCharacterReset("nilo")
        }

    fun setZetaColor(categoryKey: String, index: Int) =
        viewModelScope.launch(Dispatchers.Default) {
            userDataRepository.setZetaColor(categoryKey, index)
            analyticsRepository.logCharacterCustomized("zeta", categoryKey, index)
        }

    fun resetZetaColors() =
        viewModelScope.launch(Dispatchers.Default) {
            userDataRepository.resetZetaColors()
            analyticsRepository.logCharacterReset("zeta")
        }

    fun setMaskoColor(categoryKey: String, index: Int) =
        viewModelScope.launch(Dispatchers.Default) {
            userDataRepository.setMaskoColor(categoryKey, index)
            analyticsRepository.logCharacterCustomized("masko", categoryKey, index)
        }

    fun resetMaskoColors() =
        viewModelScope.launch(Dispatchers.Default) {
            userDataRepository.resetMaskoColors()
            analyticsRepository.logCharacterReset("masko")
        }
}
